import React, { useEffect, useRef, useState } from 'react'

import { Button } from '../../New/Button/Button'
import { Close } from '@carbon/icons-react'
import { DDSTypography } from 'den-design-system'
import Input from '../../New/Input/Input'
import RegexConstants from '../../../constants/RegexConstants'
import pallete from '../../../global/newPallete'

interface LinkCardProps {
  isOpen: boolean
  onClose: () => void
  onApply: (text: string, url: string) => void
  initialText?: string
  initialUrl?: any
  position?: { top: number; left: number }
  variant?: 'create' | 'update'
}

const LinkCard: React.FC<LinkCardProps> = ({
  isOpen,
  onClose,
  onApply,
  initialText = '',
  initialUrl = '',
  position = { top: 0, left: 0 },
  variant
}) => {
  const [text, setText] = useState(initialText)
  const [url, setUrl] = useState(initialUrl)
  const [addButtonDisabled, setAddButtonDisabled] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      setText(initialText)
      setUrl(initialUrl)
    }
  }, [isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  const handleApply = () => {
    if (url.trim()) {
      onApply(text, url)
      onClose()
    }
  }

  const verticalOffset = 300

  if (!isOpen) return null

  return (
    <div
      ref={cardRef}
      className='absolute bg-white rounded-md shadow-lg border w-[300px] px-[20px] py-[16px] z-50'
      style={{
        top:
          variant === 'create' ? position.top - verticalOffset : position.top,
        left: position.left,
        borderColor: pallete.colors.stroke2
      }}
    >
      <div className='flex justify-between items-center mb-[20px]'>
        <DDSTypography.Title
          type='h4'
          variant='bold'
          color={pallete.colors.textDark4}
        >
          Add link
        </DDSTypography.Title>
        <button onClick={onClose} className='p-[2px] rounded-full'>
          <Close size={20} color={pallete.colors.textDark6} />
        </button>
      </div>

      <div className='mb-[20px]'>
        <Input
          id='text-input'
          labelPosition='top'
          type='text'
          label={
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              className='flex items-center mr-[4px]'
              color={pallete.colors.textDark3}
            >
              Text
            </DDSTypography.Paragraph>
          }
          required={true}
          validateInput={false}
          width='260px'
          name='text'
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </div>

      <div className='mb-[20px]'>
        <Input
          id='url-input'
          value={url}
          labelPosition='top'
          type='text'
          label={
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              className='flex items-center mr-[4px]'
              color={pallete.colors.textDark3}
            >
              Link
            </DDSTypography.Paragraph>
          }
          required={true}
          validateInput={true}
          validationRegex={new RegExp(RegexConstants.REFERENCE_REGEX)}
          validate={(value: string) => {
            if (value === '') {
              return { error: false, message: '' }
            } else if (!RegexConstants.REFERENCE_REGEX.test(value)) {
              return { error: true, message: 'Please enter a valid link' }
            }
            return { error: false, message: '' }
          }}
          width='260px'
          name='link'
          onChange={(e) => {
            setUrl(e.target.value)
            setAddButtonDisabled(
              !RegexConstants.REFERENCE_REGEX.test(e.target.value)
            )
          }}
        />
      </div>

      <div className='flex justify-end items-end space-x-[8px]'>
        <Button
          id='cancel'
          size='small'
          label={
            <DDSTypography.Paragraph
              size='para'
              variant='semiBold'
              color={pallete.colors.neuButtonText}
            >
              Cancel
            </DDSTypography.Paragraph>
          }
          type='ghost'
          onClick={onClose}
        />
        <Button
          id='apply'
          size='small'
          label={
            <DDSTypography.Paragraph
              size='para'
              variant='semiBold'
              color={pallete.colors.textDark1}
            >
              Apply
            </DDSTypography.Paragraph>
          }
          type='filled'
          onClick={handleApply}
          disabled={!url.trim() || addButtonDisabled}
        />
      </div>
    </div>
  )
}

export default LinkCard
